require('./style.scss');
import { jobKey, buildableRooms, getStatus, fileUrl, hasWorlds, marbleUrl, adminOrigin } from './api';
import ModelPreview from './preview';

/**
 * "3D walkthrough" button on the published tour (prototype, branch feat/convert3d). Shown only when a model has
 * been built for this tour's floor plan in the admin ("3D model" → Build 3D); opens the first-person viewer over
 * the whole window — no report, no wall checks. One status request per tour view, only for tours with a drawn plan.
 * Sits in the tour's bottom bar right after the 3D floor plan cube (control `model3d`); a floating button only
 * when there is no bar. Opens like the 3D floor plan does: a `.e3-viewer-3dplan` layer inside the tour that fades
 * in with `show-3d`, under the tour's bottom bar, so the same bar button closes it again.
 *
 *   Model3dButton.init({ plan, getPanel, container })   // container: the tour viewer root
 */
const FADE_MS = 300;              // .e3-viewer-3dplan opacity transition
const BAR_INDEX = 5.5;            // floorplans_3d is 5
const TITLE = '3D walkthrough';
const MARBLE_INDEX = 5.6;         // right after the 3D walkthrough
const MARBLE_TITLE = '3D Marble (AI world)';
const PANEL_WAIT_MS = 10000;      // the bottom bar can be created after the status answer

function waitFor(get, ms) {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      const value = get && get();
      if (value || !get || Date.now() - start >= ms) return resolve(value || null);
      setTimeout(check, 250);
    };
    check();
  });
}

export default class Model3dButton {
  static async init({ plan, getPanel, container = document.body, api = { getStatus, fileUrl }, hostname } = {}) {
    if (!buildableRooms(plan).length) return null;
    const key = jobKey(plan, hostname);
    if (!key) return null;
    let status;
    try {
      status = await api.getStatus(key);
    } catch (e) {
      return null;   // service down / rate-limited: the tour works as before
    }
    const rooms = ((status && status.report && status.report.rooms) || []).filter(r => !r.error && r.camera);
    if (!status || status.state !== 'done' || !rooms.length) return null;
    const panel = await waitFor(getPanel, PANEL_WAIT_MS);
    const walk = new Model3dButton({ key, rooms, version: Math.round(status.updated || 0), container, api, panel });
    if (hasWorlds(status)) walk.marble = new MarbleButton({ url: marbleUrl(key, adminOrigin(hostname)), container, panel });
    return walk;
  }

  constructor({ key, rooms, version, container, api, panel }) {
    this._key = key;
    this._rooms = rooms;
    this._version = version;
    this._api = api;
    this._container = container;
    if (panel && panel.addBasicControl && panel.constructor.POSITION) {
      panel.addBasicControl('model3d', panel.constructor.POSITION.CONTAINER, BAR_INDEX, TITLE);
      panel.on('model3d', () => (this._overlay ? this.close() : this.open()));
      this._btn = panel.getControlButton('model3d').button;
      return;
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'e3-model3d-btn';
    btn.textContent = TITLE;
    btn.addEventListener('click', (e) => { e.stopPropagation(); this.open(); });
    container.appendChild(btn);
    this._btn = btn;
  }

  open() {
    if (this._overlay) return;
    const overlay = document.createElement('div');
    overlay.className = 'e3-viewer-3dplan e3-model3d';
    overlay.innerHTML = `<div class="e3-model3d__view"></div>
      <button type="button" class="e3-model3d__close" title="Close">×</button>`;
    ['mousedown', 'mouseup', 'click', 'wheel', 'touchstart', 'touchmove', 'pointerdown', 'contextmenu']
      .forEach(type => overlay.addEventListener(type, e => e.stopPropagation()));
    overlay.querySelector('.e3-model3d__close').addEventListener('click', () => this.close());
    this._onKey = (e) => { if (e.key === 'Escape' && !document.fullscreenElement) this.close(); };
    window.addEventListener('keydown', this._onKey);
    this._container.appendChild(overlay);
    this._overlay = overlay;
    this._btn.classList.add('active');
    requestAnimationFrame(() => overlay.classList.add('show-3d'));
    this._preview = new ModelPreview(overlay.querySelector('.e3-model3d__view'));
    this._preview.load(this._api.fileUrl(this._key, 'model.glb', this._version), this._rooms)
      .then(() => this._preview && this._preview.goTo(this._rooms[0].scene))
      .catch(() => {
        const msg = document.createElement('div');
        msg.className = 'e3-model3d__error';
        msg.textContent = 'The 3D model could not be loaded.';
        overlay.appendChild(msg);
      });
  }

  close() {
    if (!this._overlay) return;
    window.removeEventListener('keydown', this._onKey);
    const overlay = this._overlay;
    const preview = this._preview;
    this._overlay = null;
    this._preview = null;
    this._btn.classList.remove('active');
    overlay.classList.remove('show-3d');
    setTimeout(() => { preview.destroy(); overlay.remove(); }, FADE_MS);
  }

  remove() {
    this.close();
    this._btn.remove();
  }
}

/**
 * "3D Marble" on the published tour, next to the 3D walkthrough — only when the admin generated World Labs AI
 * worlds for the tour (the admin "3D Marble" button). Opens the world page (data/convert3d/world.html, on the admin
 * host of this environment) in an iframe, in the same in-tour layer as the walkthrough; the bar button toggles it.
 */
export class MarbleButton {
  constructor({ url, container, panel }) {
    this._url = url;
    this._container = container;
    if (panel && panel.addBasicControl && panel.constructor.POSITION) {
      panel.addBasicControl('marble3d', panel.constructor.POSITION.CONTAINER, MARBLE_INDEX, MARBLE_TITLE);
      panel.on('marble3d', () => (this._overlay ? this.close() : this.open()));
      this._btn = panel.getControlButton('marble3d').button;
      return;
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'e3-model3d-btn e3-marble3d-btn';
    btn.textContent = '3D Marble';
    btn.addEventListener('click', (e) => { e.stopPropagation(); this.open(); });
    container.appendChild(btn);
    this._btn = btn;
  }

  open() {
    if (this._overlay) return;
    const overlay = document.createElement('div');
    overlay.className = 'e3-viewer-3dplan e3-model3d e3-marble3d';
    const frame = document.createElement('iframe');
    frame.src = this._url;
    frame.allow = 'fullscreen';
    frame.title = '3D Marble';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'e3-model3d__close';
    close.title = 'Close';
    close.textContent = '×';
    close.addEventListener('click', () => this.close());
    overlay.append(frame, close);
    ['mousedown', 'mouseup', 'click', 'wheel', 'touchstart', 'touchmove', 'pointerdown', 'contextmenu']
      .forEach(type => overlay.addEventListener(type, e => e.stopPropagation()));
    this._onKey = (e) => { if (e.key === 'Escape' && !document.fullscreenElement) this.close(); };
    window.addEventListener('keydown', this._onKey);
    this._container.appendChild(overlay);
    this._overlay = overlay;
    this._btn.classList.add('active');
    requestAnimationFrame(() => overlay.classList.add('show-3d'));
  }

  close() {
    if (!this._overlay) return;
    window.removeEventListener('keydown', this._onKey);
    const overlay = this._overlay;
    this._overlay = null;
    this._btn.classList.remove('active');
    overlay.classList.remove('show-3d');
    setTimeout(() => overlay.remove(), FADE_MS);
  }
}
